import { AxiosError } from "axios";

export type AuthErrorInfo = {
  status?: number;
  // Attempts left on the current code (0 means the user must request a new code).
  remainingAttempts?: number;
  // Seconds until the user may try again (account grace period / throttle).
  lockedUntilSeconds?: number;
  // True when the current code can no longer be used and a new one must be requested.
  mustRequestNewCode: boolean;
  // True when the account exists but has no access to this application.
  noAccess: boolean;
  message: string;
};

function secondsUntil(lockedUntil?: string, retryAfter?: string): number | undefined {
  if (lockedUntil) {
    const ms = Date.parse(lockedUntil) - Date.now();
    if (!isNaN(ms)) return Math.max(0, Math.ceil(ms / 1000));
  }
  if (retryAfter) {
    const n = parseInt(retryAfter, 10);
    if (!isNaN(n)) return Math.max(0, n);
  }
  return undefined;
}

// Interprets the raw AxiosError from /api/authentication/code (the login call bypasses
// the interceptors, so e.response.status / e.response.data are authoritative).
export function parseAuthError(e: any): AuthErrorInfo {
  const resp = (e as AxiosError)?.response;
  const status = resp?.status;
  const data: any = resp?.data ?? {};
  const retryAfter = resp?.headers?.["retry-after"];
  const lockedUntilSeconds = secondsUntil(data.lockedUntil, retryAfter);

  // 423 Locked: account-level grace period — wait it out, keep the code field.
  if (status === 423) {
    return {
      status,
      lockedUntilSeconds,
      mustRequestNewCode: false,
      noAccess: false,
      message: lockedUntilSeconds != null
        ? `Too many failed attempts. Try again in ${lockedUntilSeconds}s.`
        : "Too many failed attempts. Please wait before trying again.",
    };
  }

  // 429 Too Many Requests: per-code attempts exhausted → request a new code.
  if (status === 429) {
    return {
      status,
      remainingAttempts: 0,
      lockedUntilSeconds,
      mustRequestNewCode: true,
      noAccess: false,
      message: "Too many attempts. Please request a new code.",
    };
  }

  // 400 with a remaining-attempts count: wrong / no-longer-valid code.
  if (status === 400 && typeof data.remainingAttempts === "number") {
    return {
      status,
      remainingAttempts: data.remainingAttempts,
      mustRequestNewCode: data.remainingAttempts <= 0,
      noAccess: false,
      message: data.remainingAttempts > 0
        ? `Wrong code. ${data.remainingAttempts} attempt(s) left.`
        : "No attempts left. Please request a new code.",
    };
  }

  // 400 without a count: access denied (correct code, but no role for this app).
  if (status === 400) {
    return {
      status,
      mustRequestNewCode: false,
      noAccess: true,
      message: "You don't have access to this application.",
    };
  }

  // 404: account / profile not found.
  if (status === 404) {
    return {
      status,
      mustRequestNewCode: false,
      noAccess: true,
      message: "You don't have access to this application.",
    };
  }

  return {
    status,
    mustRequestNewCode: false,
    noAccess: false,
    message: "Something went wrong, please check the code you have entered.",
  };
}
